// Shared helpers for bulk / next-month challan generation
import { FEE_CATEGORIES, FEE_COMPONENTS } from "./feeConstants";

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const fmtPKR = (n) => `PKR ${Number(n || 0).toLocaleString("en-PK")}`;
export const fmtClass = (c) => (c ? c.replace(/Grade/i, "Class").trim() : "—");
export const fmtSection = (s) => (s ? s.replace(/Section\s*/i, "").trim() : "—");
export const fmtMonthLabel = (m) => {
  if (!m || !m.includes("-")) return m || "—";
  const [y, mo] = m.split("-");
  const idx = Number(mo) - 1;
  return idx >= 0 && idx < 12 ? `${MONTHS[idx]} ${y}` : m;
};

export const loadPrevDues = (sid) => {
  try {
    return JSON.parse(localStorage.getItem(`previous_dues_${sid}`) || "[]")
      .filter((d) => !d.paid)
      .reduce((s, d) => s + (d.amount || 0), 0);
  } catch {
    return 0;
  }
};

export const loadPrevDuesList = (sid) => {
  try {
    return JSON.parse(localStorage.getItem(`previous_dues_${sid}`) || "[]").filter((d) => !d.paid);
  } catch {
    return [];
  }
};

export const nextMonthValue = (rawMonth) => {
  if (!rawMonth || !rawMonth.includes("-")) {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
  const [y, m] = rawMonth.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const defaultComponentsForCategory = (catId) => {
  const map = {
    monthly: ["tuition"],
    exam: ["examination", "tuition"],
    sports: ["sports"],
    library: ["library"],
    transport: ["transport"],
    hostel: ["hostel"],
  };
  const ids = map[catId] || [];
  return ids.map((id) => {
    const fc = FEE_COMPONENTS.find((c) => c.id === id);
    return fc
      ? { componentId: fc.id, componentLabel: fc.label, amount: fc.defaultValue }
      : null;
  }).filter(Boolean);
};

export const buildCategoryInfo = (catIds) => {
  if (!catIds?.length) return { id: "", label: "", prefix: "FEE" };
  if (catIds.length === 1) {
    const c = FEE_CATEGORIES.find((x) => x.id === catIds[0]) || { id: catIds[0], label: catIds[0], prefix: "FEE" };
    return { id: c.id, label: c.label, prefix: c.prefix };
  }
  const labels = catIds.map((id) => FEE_CATEGORIES.find((c) => c.id === id)?.label || id).filter(Boolean);
  return { id: "custom", label: labels.join(" + "), prefix: "MIX" };
};

export const buildComponentsFromCategories = (catIds, fallbackComponents = []) => {
  const seen = new Set();
  const byKey = new Map(
    (fallbackComponents || []).map((c) => [String(c.componentId || c.componentLabel).toLowerCase(), c])
  );

  return catIds.flatMap(defaultComponentsForCategory).filter((comp) => {
    const key = comp.componentLabel.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map((comp) => {
    const existing = byKey.get(String(comp.componentId).toLowerCase())
      || byKey.get(comp.componentLabel.toLowerCase());
    return existing ? { ...comp, amount: existing.amount } : comp;
  });
};

export const calcRowNetPayable = (components = [], pendingItems = [], previousDuesAmount = 0) => {
  const subtotal = (components || []).reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const pendingTotal = (pendingItems || [])
    .filter((p) => p.included !== false)
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const prev = Number(previousDuesAmount) || 0;
  return Math.max(0, subtotal) + pendingTotal + prev;
};

export const buildPendingItems = (student, unpaidFees = []) => {
  return (unpaidFees || []).map((f) => {
    const comps = f.components || [];
    const sub = comps.reduce((s, c) => s + (c.amount || 0), 0);
    const net = Math.max(0, sub - (f.concessionAmount || 0));
    return {
      id: `fee_${f.categoryId}_${f.month}`,
      type: "unpaid_fee",
      label: `${fmtMonthLabel(f.month)} (Unpaid)`,
      month: f.month,
      categoryId: f.categoryId,
      amount: net,
      included: true,
      fee: f,
    };
  });
};

export const savePreviousDuesForStudent = (studentId, amount) => {
  const lump = Number(amount) || 0;
  if (lump <= 0) {
    localStorage.setItem(`previous_dues_${studentId}`, JSON.stringify([]));
    return;
  }
  localStorage.setItem(`previous_dues_${studentId}`, JSON.stringify([
    { id: `due_${Date.now()}`, amount: lump, reason: "Previous dues", paid: false },
  ]));
};

export const buildEditStateFromRow = (row) => {
  const pendingItems = buildPendingItems(row.student, row.unpaidFees);
  const previousDuesAmount = Number(row.previousDues) || 0;
  const components = (row.components || []).map((c) => ({ ...c }));
  const netPayable = calcRowNetPayable(components, pendingItems, previousDuesAmount);

  return {
    ...row,
    selectedCategories: [...(row.selectedCategories || [])],
    components,
    pendingItems,
    previousDuesAmount,
    netPayable,
  };
};

const studentFees = (student, feeAssignments = {}) =>
  Object.values(feeAssignments[student.id] || feeAssignments[String(student.id)] || {}).filter(Boolean);

/** Latest month that is paid/waived or already has a generated challan */
export const getLatestAnchorMonth = (student, feeAssignments = {}, challanMap = new Map()) => {
  const sid = student.id;
  let latest = "";
  studentFees(student, feeAssignments).forEach((f) => {
    const isPaid = f.status === "Paid" || f.status === "Waived";
    const hasChallan = challanMap.has(`${sid}|${f.categoryId}_${f.month}`);
    if ((isPaid || hasChallan) && (!latest || f.month > latest)) latest = f.month;
  });
  return latest;
};

/** True when the month is paid/waived or already has a generated challan */
export const isMonthAlreadyGeneratedOrPaid = (student, month, feeAssignments = {}, challanMap = new Map()) => {
  if (!month) return false;
  const sid = student.id;
  const monthFees = studentFees(student, feeAssignments).filter((f) => f.month === month);
  if (monthFees.length && monthFees.every((f) => f.status === "Paid" || f.status === "Waived")) return true;
  return monthFees.some((f) => challanMap.has(`${sid}|${f.categoryId}_${f.month}`));
};

/** Forward months after the last paid/generated month, skipping months that already exist */
export const getEligibleBulkMonthsForStudent = (student, feeAssignments = {}, challanMap = new Map(), maxForward = 6) => {
  const anchor = getLatestAnchorMonth(student, feeAssignments, challanMap);
  let cursor = anchor
    ? nextMonthValue(anchor)
    : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;

  const months = [];
  let guard = 0;
  while (months.length < maxForward && guard < 24) {
    guard += 1;
    if (!isMonthAlreadyGeneratedOrPaid(student, cursor, feeAssignments, challanMap)) {
      months.push(cursor);
    }
    cursor = nextMonthValue(cursor);
  }
  return months;
};

export const buildEligibleBulkMonthOptions = (students = [], feeAssignments = {}, challanMap = new Map(), maxForward = 6) => {
  const values = new Set();
  students.forEach((student) => {
    getEligibleBulkMonthsForStudent(student, feeAssignments, challanMap, maxForward).forEach((m) => values.add(m));
  });
  return [...values].sort().map((value) => ({ value, label: fmtMonthLabel(value) }));
};

export const resolveDefaultBulkMonth = (students, feeAssignments, challanMap, rows = []) => {
  const options = buildEligibleBulkMonthOptions(students, feeAssignments, challanMap);
  if (!options.length) return { options, defaultMonth: "" };

  const preferred = rows.map((r) => r.targetMonth).find((m) => m && options.some((o) => o.value === m));
  return { options, defaultMonth: preferred || options[0].value };
};

/** Build editable row data for one student before/after generation */
export const buildStudentBulkRow = (student, feeAssignments = {}, challanMap = new Map()) => {
  const fees = feeAssignments[student.id] || feeAssignments[String(student.id)] || {};
  const feeList = Object.values(fees)
    .filter(Boolean)
    .sort((a, b) => (b.month || "").localeCompare(a.month || ""));

  const unpaidFees = feeList.filter((f) => f.status !== "Paid" && f.status !== "Waived");
  const latestPaidFee = feeList.find((f) => f.status === "Paid" || f.status === "Waived") || null;
  const latestFee = latestPaidFee || feeList[0] || null;
  const previousDues = loadPrevDues(student.id);
  const previousDuesList = loadPrevDuesList(student.id);

  let targetMonth = "";
  let selectedCategories = [];
  let components = [];
  let dueDate = "10";
  let academicYear = "2025-2026";
  let categoryId = "";
  let categoryLabel = "";
  let categoryPrefix = "FEE";
  let sourceMode = "none";

  const anchorMonth = getLatestAnchorMonth(student, feeAssignments, challanMap);
  const templateFee = latestPaidFee || (anchorMonth ? feeList.find((f) => f.month === anchorMonth) : null) || latestFee;

  if (templateFee || anchorMonth) {
    const immediateNextMonth = anchorMonth
      ? nextMonthValue(anchorMonth)
      : nextMonthValue(templateFee.month);
    const openUnpaid = unpaidFees.filter(
      (f) => !isMonthAlreadyGeneratedOrPaid(student, f.month, feeAssignments, challanMap)
    );
    const blockingUnpaid = openUnpaid.find((f) => f.month === immediateNextMonth)
      || [...openUnpaid].sort((a, b) => (a.month || "").localeCompare(b.month || ""))[0]
      || null;

    if (blockingUnpaid) {
      targetMonth = blockingUnpaid.month;
      selectedCategories = blockingUnpaid.selectedCategories || [blockingUnpaid.categoryId].filter(Boolean);
      components = (blockingUnpaid.components || []).map((c) => ({ ...c }));
      dueDate = blockingUnpaid.dueDate || "10";
      academicYear = blockingUnpaid.academicYear || academicYear;
      categoryId = blockingUnpaid.categoryId;
      categoryLabel = blockingUnpaid.categoryLabel;
      categoryPrefix = blockingUnpaid.categoryPrefix || "FEE";
      sourceMode = "existing_next";
    } else {
      targetMonth = immediateNextMonth;
      selectedCategories = templateFee.selectedCategories || [templateFee.categoryId].filter(Boolean);
      components = (templateFee.components || []).map((c) => ({ ...c }));
      dueDate = templateFee.dueDate || "10";
      academicYear = templateFee.academicYear || academicYear;
      const info = buildCategoryInfo(selectedCategories);
      categoryId = selectedCategories.length > 1 ? "custom" : selectedCategories[0];
      categoryLabel = info.label;
      categoryPrefix = info.prefix;
      sourceMode = "next_month";
    }
  }

  const subtotal = components.reduce((s, c) => s + (c.amount || 0), 0);
  const pendingFromUnpaid = unpaidFees
    .filter((f) => f.month !== targetMonth)
    .reduce((s, f) => {
      const comps = f.components || [];
      const sub = comps.reduce((a, c) => a + (c.amount || 0), 0);
      return s + Math.max(0, sub - (f.concessionAmount || 0));
    }, 0);
  const netPayable = Math.max(0, subtotal) + previousDues + pendingFromUnpaid;

  return {
    student,
    targetMonth,
    selectedCategories,
    components,
    dueDate,
    academicYear,
    categoryId,
    categoryLabel,
    categoryPrefix,
    sourceMode,
    unpaidFees,
    previousDues,
    previousDuesList,
    subtotal,
    netPayable,
    latestFee: templateFee || latestFee,
  };
};
